import { BreadcrumbList } from 'schema-dts';
import { helmetJsonLdProp } from 'react-schemaorg';
import { ComponentRendering } from '@sitecore-jss/sitecore-jss-react';
import { TJsonLDObj, TSitecoreContext } from '../../../types/types';
import {
  TNavigationBarBreadcrumbItem,
  TNavigationBarFields,
  TNavigationBarParams,
  TNavigationBarProps,
} from '../types/productBreadcrumbBar.types';

const getBreadCrumbAbsoluteUrls = (origin = '', itemUrl = ''): string => {
  // function to remove the virtual folder name from origin as it is also appearing in breadcrumb relative url
  // and form an absolute url
  const itemSubRouteNames = itemUrl.split('/');
  const removeStrings: string[] = [];
  origin
    .replace('https://', '')
    .split(/\//)
    .forEach((value) => value && itemSubRouteNames.includes(value) && removeStrings.push(value));
  const filteredOrigin = removeStrings.reduce((acc, value) => acc.replace('/' + value, ''), origin);
  return filteredOrigin + itemUrl;
};

export class NavigationBarModel {
  private readonly fields?: TNavigationBarFields;
  private readonly context?: TSitecoreContext;
  private readonly rendering?: ComponentRendering;
  private readonly params?: TNavigationBarParams;

  constructor(props: TNavigationBarProps) {
    this.fields = props?.fields || null;
    this.context = props?.sitecoreContext || null;
    this.rendering = props?.rendering;
    this.params = props?.params;
  }

  get thumbnailEE(): string {
    return this.fields?.itemData?.thumbnail || '';
  }

  get componentName(): string {
    return this.rendering?.componentName || '';
  }

  get breadcrumbItems(): Array<TNavigationBarBreadcrumbItem> {
    const productItem: TNavigationBarBreadcrumbItem = {
      text: this.productsPageLabel || '',
      url: this.productsPageUrl || '',
    };

    const apiBreadcrumbItems = this.fields?.apiData?.breadcrumbs || [];

    return [productItem, ...apiBreadcrumbItems];
  }

  get productsPageLabel(): string | undefined {
    return this.fields?.itemData?.fields?.productsPageTitle;
  }

  get productsPageUrl(): string | undefined {
    return this.fields?.itemData?.fields?.productsPageLink;
  }

  get backButtonLabel(): string | undefined {
    return this.fields?.itemData?.fields?.backButtonText;
  }

  get backButtonUrl(): string | undefined {
    return this.fields?.itemData?.fields?.backButtonUrl;
  }

  get originUrl(): string {
    return this.context?.hostVirtualFolder || '';
  }

  get textColor(): string {
    return this.params?.textColor || 'White';
  }

  get breadcrumbJSONLD(): TJsonLDObj {
    return helmetJsonLdProp<BreadcrumbList>({
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: this.breadcrumbItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.text,
        item: getBreadCrumbAbsoluteUrls(this.originUrl, item.url),
      })),
    });
  }

  get isUnreleased(): boolean {
    return !!this.fields?.isComponentUnreleased;
  }
}
